import { z } from 'zod'
import { SPECIALIZATION_VALUES } from '@/config/specializations'
import { RESERVED_USERNAMES } from '@/config/reserved-usernames'

/**
 * Username rules: 3–30 chars, lowercase alphanumeric + underscore + hyphen.
 * Per spec, hyphens are allowed (e.g. `andrei-popescu`); leading/trailing
 * hyphens are rejected to keep slugs URL-clean.
 * Checked against reserved list. DB UNIQUE constraint is final arbiter.
 */
export const usernameSchema = z
  .string()
  .trim()
  .min(3, 'Username must be at least 3 characters')
  .max(30, 'Username must be at most 30 characters')
  .regex(
    /^[a-z0-9_](?:[a-z0-9_-]*[a-z0-9_])?$/,
    'Only lowercase letters, numbers, underscores, and hyphens (not leading or trailing)',
  )
  .refine((val) => !RESERVED_USERNAMES.has(val), {
    message: 'This username is reserved',
  })

/** Onboarding Step 1: username (required) */
export const stepUsernameSchema = z.object({
  username: usernameSchema,
})

/** Onboarding Step 2: display name (required) */
export const stepDisplayNameSchema = z.object({
  display_name: z
    .string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be at most 50 characters'),
})

/** Onboarding Step 4: bio (optional) */
export const stepBioSchema = z.object({
  bio: z
    .string()
    .trim()
    .max(280, 'Bio must be at most 280 characters')
    .optional()
    .or(z.literal('')),
})

/** Onboarding Step 5: specializations (1–5 required) */
export const stepSpecializationsSchema = z.object({
  specializations: z
    .array(z.enum(SPECIALIZATION_VALUES))
    .min(1, 'Select at least 1 specialization')
    .max(5, 'Select at most 5 specializations'),
})

/** Profile edit (post-onboarding): all fields optional */
export const profileEditSchema = z.object({
  username: usernameSchema.optional(),
  display_name: z.string().trim().min(2).max(50).optional(),
  bio: z.string().trim().max(500).nullable().optional(),
  specializations: z
    .array(z.enum(SPECIALIZATION_VALUES))
    .min(1)
    .max(5)
    .optional(),
  languages: z.array(z.string().trim().max(10)).max(10).default([]),
  timezone: z.string().trim().max(100).optional(),
})

// ─── Social Links Schema ────────────────────────────────────

const safeUrlSchema = z
  .string()
  .url()
  .refine(
    (val) => {
      try {
        const parsed = new URL(val)
        return ['http:', 'https:'].includes(parsed.protocol)
      } catch {
        return false
      }
    },
    { message: 'Only http and https URLs are allowed' },
  )

const urlOrEmpty = safeUrlSchema.or(z.literal('')).optional()

/** Validated social links object */
export const socialLinksSchema = z
  .object({
    twitter: urlOrEmpty,
    github: urlOrEmpty,
    linkedin: urlOrEmpty,
    website: urlOrEmpty,
    portfolio: urlOrEmpty,
  })
  .strict()

// ─── Profile Page Update Schema ─────────────────────────────

/** Rate type options for freelancer profiles */
export const RATE_TYPES = ['hourly', 'project', 'monthly'] as const

/**
 * Availability status (D1.4 - two-axis model).
 * Replaces the old 5-status LinkedIn-derived list with a 3-value status
 * column plus the orthogonal `LOOKING_FOR_OPTIONS` chip array.
 */
export const AVAILABILITY_OPTIONS = ['available', 'in_a_project', 'closed'] as const

/** "Looking for" chips orthogonal to status (D1.4). 0..3 chips. */
export const LOOKING_FOR_OPTIONS = [
  'long_term_collaborations',
  'quick_gigs',
  'open_to_talk',
] as const

/**
 * Profile Page update - comprehensive schema for /u/[username] editing.
 * Extends profileEditSchema with all profile page fields.
 * All fields optional (partial update).
 * Ref: profile-page task §3
 */
export const profilePageUpdateSchema = z
  .object({
    username: usernameSchema.optional(),
    display_name: z.string().trim().min(2).max(50).optional(),
    headline: z
      .string()
      .trim()
      .max(80, 'Headline must be at most 80 characters')
      .nullable()
      .optional(),
    bio: z.string().trim().max(500).nullable().optional(),
    location: z
      .string()
      .trim()
      .max(100, 'Location must be at most 100 characters')
      .nullable()
      .optional(),
    specializations: z
      .array(z.enum(SPECIALIZATION_VALUES))
      .min(1)
      .max(5)
      .optional(),
    tools: z.array(z.string().trim().max(50)).max(10).optional(),
    rate_type: z.enum(RATE_TYPES).nullable().optional(),
    rate_min: z
      .number()
      .min(0, 'Rate must be positive')
      .max(999999.99)
      .nullable()
      .optional(),
    rate_max: z
      .number()
      .min(0, 'Rate must be positive')
      .max(999999.99)
      .nullable()
      .optional(),
    availability: z.enum(AVAILABILITY_OPTIONS).optional(),
    looking_for: z.array(z.enum(LOOKING_FOR_OPTIONS)).max(3).optional(),
    social_links: socialLinksSchema.optional(),
    pinned_prompt_ids: z
      .array(z.string().uuid())
      .max(6, 'At most 6 pinned prompts')
      .optional(),
    is_public: z.boolean().optional(),
    languages: z.array(z.string().trim().max(10)).max(10).default([]),
    timezone: z.string().trim().max(100).nullable().optional(),
  })
  .refine(
    (data) => {
      if (data.rate_min != null && data.rate_max != null) {
        return data.rate_max >= data.rate_min
      }
      return true
    },
    {
      message: 'Maximum rate must be greater than or equal to minimum rate',
      path: ['rate_max'],
    },
  )

// ─── Wizard Step Schemas (new 5-step flow) ────────────────────

/** AI tools taxonomy - used in Step 2 */
export const AI_TOOLS = [
  'ChatGPT', 'Claude', 'Gemini', 'Midjourney', 'DALL-E',
  'Stable Diffusion', 'Copilot', 'Perplexity', 'Zapier AI', 'LangChain',
  'Other',
] as const

/** Profile completion weights - used in Step 5 celebration */
// FTUE redesign: following is optional, so it no longer counts toward profile
// completion. Its old 15% is redistributed so weights still sum to 100.
export const COMPLETION_WEIGHTS = {
  avatar: 15,
  bio: 15,
  specializations: 15,
  first_prompt: 40,
  verified_email: 15,
} as const

/**
 * Wizard Step 1 - Identity (display_name required, headline + avatar optional)
 * Ref: onboarding-wizard-technical-design §3.1
 */
export const wizardStep1Schema = z.object({
  display_name: z
    .string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be at most 50 characters'),
  headline: z
    .string()
    .trim()
    .max(80, 'Headline must be at most 80 characters')
    .optional()
    .or(z.literal('')),
  avatar_url: z
    .string()
    .url('Invalid URL')
    .refine((url) => /^https?:\/\//i.test(url), { message: 'URL must use http or https' })
    .optional()
    .or(z.literal('')),
})

/**
 * Wizard Step 2 - Specializations + Tools (min 1 specialization required)
 * Ref: onboarding-wizard-technical-design §3.2
 */
export const wizardStep2Schema = z.object({
  specializations: z
    .array(z.enum(SPECIALIZATION_VALUES))
    .min(1, 'Select at least 1 specialization')
    .max(5, 'Select at most 5 specializations'),
  tools: z
    .array(z.string().trim().max(50))
    .max(20)
    .default([]),
})

/**
 * Wizard Step 3 - Prompt Import (optional, saved to localStorage only)
 * Ref: onboarding-wizard-technical-design §3.3
 */
export const wizardStep3Schema = z.object({
  prompt_text: z.string().max(10000).optional().or(z.literal('')),
  prompt_title: z.string().max(100, 'Title must be at most 100 characters').optional().or(z.literal('')),
  skipped: z.preprocess((v) => v === 'true' || v === true, z.boolean()).default(false),
})

/**
 * Calculate profile completion percentage.
 * Ref: onboarding-wizard-feature-brief AC-502
 */
export function calculateCompletion(profile: {
  avatar_url?: string | null
  bio?: string | null
  specializations?: string[] | null
  has_prompt?: boolean
  follower_count?: number
  email_verified?: boolean
}): number {
  let score = 0
  if (profile.avatar_url) score += COMPLETION_WEIGHTS.avatar
  if (profile.bio) score += COMPLETION_WEIGHTS.bio
  if (profile.specializations && profile.specializations.length > 0) score += COMPLETION_WEIGHTS.specializations
  if (profile.has_prompt) score += COMPLETION_WEIGHTS.first_prompt
  if (profile.email_verified) score += COMPLETION_WEIGHTS.verified_email
  return score
}

export type StepUsernameInput = z.infer<typeof stepUsernameSchema>
export type StepDisplayNameInput = z.infer<typeof stepDisplayNameSchema>
export type StepBioInput = z.infer<typeof stepBioSchema>
export type StepSpecializationsInput = z.infer<typeof stepSpecializationsSchema>
export type ProfileEditInput = z.infer<typeof profileEditSchema>
export type WizardStep1Input = z.infer<typeof wizardStep1Schema>
export type WizardStep2Input = z.infer<typeof wizardStep2Schema>
export type WizardStep3Input = z.infer<typeof wizardStep3Schema>
export type ProfilePageUpdateInput = z.infer<typeof profilePageUpdateSchema>
export type SocialLinksInput = z.infer<typeof socialLinksSchema>
